import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF, Text, Html } from '@react-three/drei'
import * as THREE from 'three'

/**
 * AnnotationPoint Component
 * 
 * Arrow model positioned on the phone screen mesh with offset from center.
 * Hovers slightly above the screen to prevent z-fighting.
 * This component is rendered as a CHILD of the phone group, so it inherits
 * the phone's transformations and moves with it.
 * 
 * @param {object} phoneRef - Reference to the phone group containing the screen mesh
 * @param {number} offsetX - Horizontal offset from screen center (-0.5 to 0.5 recommended, 0 = center)
 * @param {number} offsetY - Vertical offset from screen center (-0.5 to 0.5 recommended, 0 = center)
 * @param {string} annotationType - Type of annotation: 'issue', 'success', 'focus', 'default'
 * @param {string} direction - Arrow direction: 'left', 'top', 'right', 'bottom' (default: 'left')
 * @param {string} title - Annotation title text
 * @param {boolean} showAnnotationBox - Whether to show the annotation box
 * @param {boolean} showDebugGrid - Show grid overlay to help position annotations
 */
export function AnnotationPoint({ 
  phoneRef, 
  offsetX = 0, 
  offsetY = 0, 
  annotationType = 'focus', 
  direction = 'left', 
  title = '',
  description = '',
  showAnnotationBox = false,
  showDebugGrid = false 
}) {
  const arrowRef = useRef()
  const [screenPosition, setScreenPosition] = useState(null)
  const [screenDimensions, setScreenDimensions] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  
  // Get style configuration based on annotation type (matching intro-tag-pill styles)
  const getAnnotationStyle = (type, isOpen, isHovered) => {
    const baseStyle = {
      fontFamily: "'Doto', sans-serif",
      fontSize: '1.325rem', // label-large equivalent
      fontWeight: '900',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: 'white',
      display: 'inline-flex',
      alignItems: 'flex-start',
      gap: '1rem', // spacing between icon and text
      width: `48ch`,
      maxWidth: isOpen ? '48ch' : 'fit-content',
      cursor: 'pointer',
      transition: 'max-width 0.3s cubic-bezier(0.33, 1, 0.68, 1)',
      overflow: 'hidden'
    }
    
    // Type-specific backgrounds matching intro-tag-pill
    const typeStyles = {
      'issue': {
        background: '#ff5500',
        color: 'white'
      },
      'success': {
        background: '#00ffa6',
        color: '#1a1a1a' // var(--text-dark)
      },
      'error': {
        background: '#ff0033',
        color: 'white'
      },
      'focus': {
        background: '#004cff',
        color: 'white'
      },
      'default': {
        background: 'transparent',
        border: '1px solid #ffffff',
        color: 'white'
      }
    }
    
    return { ...baseStyle, ...(typeStyles[type] || typeStyles.default) }
  }
  
  // Material Symbols icon style (matching app's material-symbols-rounded)
  const getIconStyle = (isOpen) => ({
    fontFamily: "'Material Symbols Rounded'",
    fontWeight: 'bold',
    fontStyle: 'normal',
    fontSize: '24px',
    padding: '0.25rem 0.25rem',
    lineHeight: '1',
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'inline-block',
    WebkitFontSmoothing: 'antialiased',
    // Add custom styles when open
    ...(isOpen && {
      // padding: '0.75rem 0.75rem',
    })
  })
  
  // Direction rotation mapping (rotation around local Z axis)
  // Arrow model default orientation: pointing down (-Y in Blender)
  const getRotationByDirection = (dir) => {
    const rotations = {
      'bottom': Math.PI,          // Point up (180° from down)
      'top': 0,             // Point down (default, 0°)
      'right': Math.PI / 2,     // Point left (90°)
      'left': -Math.PI / 2    // Point right (-90°)
    }
    return rotations[dir] || 0
  }
  
  const directionRotation = getRotationByDirection(direction)
  
  // Load the arrow model
  const { scene: arrowModel } = useGLTF('/models/model-prop-annotation-arrow-1.3.glb')
  
  // Clone the model once on mount and modify emissive material
  const arrowClone = useMemo(() => {
    const clone = arrowModel.clone()
    
    // Get color from style configuration
    const styleConfig = getAnnotationStyle(annotationType)
    const emissiveColor = styleConfig.background !== 'transparent' 
      ? styleConfig.background 
      : styleConfig.color
    
    // Traverse and modify emissive material on all meshes
    clone.traverse((child) => {
      if (child.isMesh) {
        // Modify existing material or create new one
        if (child.material) {
          // Clone the material to avoid modifying the original
          child.material = child.material.clone()
          // Update emissive properties based on annotation type
          child.material.color.set(emissiveColor)
          child.material.emissive.set(emissiveColor)
          child.material.emissiveIntensity = 2.6
        }
      }
    })
    
    return clone
  }, [arrowModel, annotationType])
  
  const screenMeshRef = useRef(null)
  const [screenMeshReady, setScreenMeshReady] = useState(false)
  
  // Find the screen mesh once on mount
  useEffect(() => {
    if (!phoneRef?.current) {
      return
    }
    
    let foundScreen = false
    
    phoneRef.current.traverse((child) => {
      if (child.isMesh && child.name.toLowerCase().includes('screen')) {
        screenMeshRef.current = child
        foundScreen = true
        setScreenMeshReady(true)
        console.log('✅ AnnotationPoint found screen mesh:', child.name, 'for annotation:', title)
      }
    })
    
    if (!foundScreen) {
      console.warn('⚠️ AnnotationPoint: No screen mesh found yet for:', title)
    }
  }, [phoneRef, title])
  
  // Recalculate position whenever screen mesh is found or offsets change
  useEffect(() => {
    const screenMesh = screenMeshRef.current
    if (!screenMesh || !screenMeshReady) {
      return // Don't calculate until screen mesh is available
    }
    
    // Get the screen's LOCAL position and rotation within the phone group
    const localPos = screenMesh.position.clone()
    const localRot = screenMesh.rotation.clone()
    
    // Get screen dimensions from geometry bounding box
    screenMesh.geometry.computeBoundingBox()
    const bbox = screenMesh.geometry.boundingBox
    const width = bbox.max.x - bbox.min.x
    const height = bbox.max.y - bbox.min.y
    
    setScreenDimensions({ width, height })
    
    // Create offset in screen-local space (XY plane)
    const offsetVector = new THREE.Vector3(
      offsetX * width,  // X offset
      offsetY * height, // Y offset
      0.01            // Small Z offset to prevent z-fighting
    )
    
    // Apply screen's rotation to the offset vector
    offsetVector.applyEuler(localRot)
    
    // Add offset to screen's center position
    const finalPos = localPos.clone().add(offsetVector)
    
    setScreenPosition({
      position: finalPos,
      rotation: localRot,
      center: localPos.clone() // Store unmodified center for debug grid
    })
    
    console.log('📍 AnnotationPoint positioned:', title, {
      screen: screenMesh.name,
      offset: [offsetX, offsetY],
      finalPosition: finalPos.toArray()
    })
  }, [offsetX, offsetY, title, screenMeshReady])
  
  // Update arrow position every frame to match screen
  useFrame(() => {
    if (arrowRef.current && screenPosition) {
      arrowRef.current.position.copy(screenPosition.position)
      
      // Apply rotations: copy screen rotation, then apply direction rotation on Z-axis only
      arrowRef.current.rotation.copy(screenPosition.rotation)
      arrowRef.current.rotation.z += directionRotation  // Rotate around Z-axis for direction
      
      // move arrow slightly forward along its local Z axis to hover above screen
      arrowRef.current.position.z += 0.1 // Extra small offset to ensure visibility
    }
  })
  
  // Don't render anything until we have a valid position
  if (!screenPosition || !title) {
    return null
  }
  
  // Get the style for this annotation type
  const annotationStyle = getAnnotationStyle(annotationType, isOpen, isHovered)
  
  // Handle hover events to open/close annotation
  const handleMouseEnter = () => {
    setIsHovered(true)
    setIsOpen(true)
  }
  
  const handleMouseLeave = () => {
    setIsHovered(false)
    setIsOpen(false)
  }
  
  return (
    <>
      {/* Temporarily disabled 3D arrow */}
      {/* <primitive 
        ref={arrowRef}
        object={arrowClone}
        scale={0.75}
      /> */}
      
      {/* HTML Annotation - tracks the 3D position */}
      {screenPosition && title && (
        <Html
          position={[
            screenPosition.position.x,
            screenPosition.position.y,
            screenPosition.position.z
          ]}
          center={false}
          transform
          distanceFactor={0.0625}
          occlude={false}
          zIndexRange={isOpen ? [1000, 0] : [100, 0]}
          style={{
            pointerEvents: 'auto',
            zIndex: isOpen ? 1000 : 100
          }}
        >
          <div style={{
            position: 'absolute',
            transformOrigin: 'left top'
          }}>
            <span 
              style={annotationStyle}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {/* Icon - changes based on open/closed state */}
              <span style={getIconStyle(isOpen)}>
                {isOpen ? 'collapse_content' : 'add'}
              </span>
              
              {/* Label - only visible when open */}
              {isOpen && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  padding: '0.25rem 0.25rem',
                  opacity: 0,
                  transform: 'translateX(-10px)',
                  animation: 'fadeInSlide 0.3s cubic-bezier(0.33, 1, 0.68, 1) forwards'
                }}>
                  <span>{title}</span>
                  <span>-------------------------</span>
                  {description && (
                    <span style={{
                      fontSize: '1.5rem',
                      fontWeight: '900',
                      textTransform: 'none',
                    }}>{description}</span>
                  )}
                </div>
              )}
            </span>
          </div>
        </Html>
      )}
      
      
    </>
  )
}

// Preload the arrow model
useGLTF.preload('/models/model-prop-annotation-arrow-1.3.glb')
